<template>
  <review-detail
    v-if="selectedId"
    :key="selectedId"
    :review-id="selectedId"
    :employees="employees"
    @close="selectedId = null"
    @changed="load"
  />
  <section v-else class="sp-panel">
    <div class="sp-row sp-between">
      <div>
        <h2>My Reviews</h2>
        <p class="sp-muted">
          Reflect on your work, discuss next steps, and keep a record of your
          progress.
        </p>
      </div>
      <a class="sp-link" :href="localUrl(legacyUrl)"
        >View earlier performance reviews</a
      >
    </div>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!creating"
      :success="success"
      @retry="load"
    />
    <div class="sp-filter" aria-label="Review scope">
      <button
        type="button"
        :aria-pressed="scope === 'mine'"
        @click="changeScope('mine')"
      >
        My reviews</button
      ><button
        type="button"
        :aria-pressed="scope === 'assigned'"
        @click="changeScope('assigned')"
      >
        Reviews assigned to me</button
      ><button
        v-if="isAdmin"
        type="button"
        :aria-pressed="scope === 'manage'"
        @click="changeScope('manage')"
      >
        Manage review assignments
      </button>
    </div>
    <div v-if="canCreate && !creating" class="sp-actions">
      <oxd-button
        label="Start a review"
        display-type="secondary"
        @click="creating = true"
      />
    </div>
    <form
      v-if="creating && canCreate"
      class="sp-notice"
      @submit.prevent="create"
      @input="dirty = true"
    >
      <h3>Start an annual review</h3>
      <p>
        The listed supervisor is selected when there is one match. Confirm the
        employee, template, and dates before creating the review.
      </p>
      <fieldset :disabled="busy">
        <div class="sp-grid">
          <label class="sp-field"
            >Employee<select
              v-model.number="draft.empNumber"
              required
              @change="chooseEmployee"
            >
              <option value="">Select an employee</option>
              <option
                v-for="employee in employees"
                :key="employee.empNumber"
                :value="employee.empNumber"
              >
                {{ employee.name }}
              </option>
            </select></label
          >
          <label class="sp-field"
            >Assigned supervisor<select
              v-model.number="draft.reviewerEmpNumber"
              required
            >
              <option value="">Select a supervisor</option>
              <option
                v-for="employee in reviewerOptions"
                :key="employee.empNumber"
                :value="employee.empNumber"
              >
                {{ employee.name }}
              </option></select
            ><small>{{ supervisorNote }}</small></label
          >
          <label class="sp-field"
            >Review template<select v-model="draft.templateId" required>
              <option
                v-for="template in templates"
                :key="template.id"
                :value="template.id"
              >
                {{ template.title }} · {{ template.questions.length }} prompts
              </option>
            </select></label
          >
          <label class="sp-field"
            >Period start<input
              v-model="draft.periodStart"
              type="date"
              required
          /></label>
          <label class="sp-field"
            >Period end<input
              v-model="draft.periodEnd"
              type="date"
              :min="draft.periodStart"
              required
          /></label>
          <label class="sp-field"
            >Self review due<input v-model="draft.dueDate" type="date" required
          /></label>
        </div>
        <div class="sp-actions">
          <oxd-button
            type="submit"
            label="Create review"
            display-type="secondary"
            :disabled="busy"
          /><button type="button" class="sp-link" @click="cancelCreate">
            Cancel
          </button>
        </div>
      </fieldset>
    </form>
    <p v-if="scope === 'manage'" class="sp-notice">
      This list shows review progress and assignments. Only the employee and
      assigned supervisor can read the review responses.
    </p>
    <p v-if="!loading && !items.length" class="sp-empty">
      {{
        scope === 'mine'
          ? 'You do not have an annual review yet. Reviews will appear here when they are assigned.'
          : scope === 'assigned'
          ? 'No reviews are currently assigned to you.'
          : 'No annual reviews have been created yet.'
      }}
    </p>
    <div v-if="items.length" class="sp-table-wrap">
      <table class="sp-table">
        <thead>
          <tr>
            <th scope="col">Employee / period</th>
            <th scope="col">Supervisor</th>
            <th scope="col">Due</th>
            <th scope="col">Status</th>
            <th scope="col"><span class="sp-muted">Details</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="review in items" :key="review.id">
            <td>
              <strong>{{ review.employeeName }}</strong
              ><br />{{ displayDate(review.periodStart) }} –
              {{ displayDate(review.periodEnd) }}
            </td>
            <td>{{ review.reviewerName }}</td>
            <td>{{ displayDate(review.dueDate) }}</td>
            <td>
              <span class="sp-badge">{{ statusLabel(review.status) }}</span>
            </td>
            <td>
              <button
                type="button"
                class="sp-link"
                :aria-label="`Open review for ${review.employeeName}`"
                @click="openReview(review.id)"
              >
                Open review
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="total > limit" class="sp-actions">
      <button
        class="sp-link"
        type="button"
        :disabled="!offset || loading"
        @click="page(-1)"
      >
        Previous</button
      ><span
        >{{ offset + 1 }}–{{ Math.min(offset + limit, total) }} of
        {{ total }}</span
      ><button
        class="sp-link"
        type="button"
        :disabled="offset + limit >= total || loading"
        @click="page(1)"
      >
        Next
      </button>
    </div>
  </section>
</template>

<script>
import {ref, computed, onMounted} from 'vue';
import ReviewDetail from '../components/ReviewDetail.vue';
import PortalFeedback from '../components/PortalFeedback.vue';
import {
  portalApi,
  errorMessage,
  displayDate,
  statusLabel,
  localUrl,
  useDraftGuard,
} from '../portal';
const newDraft = () => ({
  empNumber: '',
  reviewerEmpNumber: '',
  templateId: 'staff',
  periodStart: '',
  periodEnd: '',
  dueDate: '',
});
export default {
  components: {ReviewDetail, PortalFeedback},
  setup() {
    const api = portalApi('reviews');
    const items = ref([]),
      employees = ref([]),
      templates = ref([]),
      scope = ref('mine'),
      total = ref(0),
      offset = ref(0);
    const selectedId = ref(null),
      isAdmin = ref(false),
      canCreate = ref(false),
      legacyUrl = ref('/performance/myPerformanceReview');
    const loading = ref(false),
      busy = ref(false),
      creating = ref(false),
      dirty = ref(false),
      error = ref(''),
      success = ref(''),
      draft = ref(newDraft());
    const {canLeave} = useDraftGuard(dirty);
    const limit = 25;
    let generation = 0;
    const selectedEmployee = computed(() =>
      employees.value.find(
        (employee) => employee.empNumber === draft.value.empNumber,
      ),
    );
    const reviewerOptions = computed(() =>
      employees.value.filter(
        (employee) => employee.empNumber !== draft.value.empNumber,
      ),
    );
    const supervisorNote = computed(() =>
      selectedEmployee.value?.supervisors?.length === 1
        ? 'Defaulted to the employee’s listed supervisor. Administrators may choose another supervisor.'
        : 'Choose a supervisor; there is no single listed supervisor for this employee.',
    );
    async function load() {
      const request = ++generation;
      loading.value = true;
      error.value = '';
      items.value = [];
      try {
        const data = await api.list({
          scope: scope.value,
          limit,
          offset: offset.value,
        });
        if (request !== generation) return;
        items.value = data.items;
        total.value = data.total;
        isAdmin.value = data.isAdmin;
        canCreate.value = data.canCreate;
        employees.value = data.employees || [];
        templates.value = data.templates || [];
        legacyUrl.value = data.legacyUrl || legacyUrl.value;
      } catch (e) {
        if (request === generation) error.value = errorMessage(e);
      } finally {
        if (request === generation) loading.value = false;
      }
    }
    function changeScope(value) {
      if (value === 'manage' && !isAdmin.value) return;
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        scope.value = value;
        offset.value = 0;
        load();
      }
    }
    function page(direction) {
      offset.value = Math.max(0, offset.value + limit * direction);
      load();
    }
    function openReview(id) {
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        selectedId.value = id;
      }
    }
    function cancelCreate() {
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
      }
    }
    function chooseEmployee() {
      const employee = selectedEmployee.value;
      draft.value.reviewerEmpNumber =
        employee?.supervisors?.length === 1 ? employee.supervisors[0] : '';
      draft.value.templateId = employee?.suggestedTemplate || 'staff';
      dirty.value = true;
    }
    async function create() {
      if (busy.value || !canCreate.value) return;
      if (
        !draft.value.empNumber ||
        !draft.value.reviewerEmpNumber ||
        !draft.value.periodStart ||
        !draft.value.periodEnd ||
        !draft.value.dueDate
      ) {
        error.value = 'Complete the employee, supervisor, and review dates.';
        return;
      }
      if (draft.value.periodEnd < draft.value.periodStart) {
        error.value = 'The review period must end on or after its start date.';
        return;
      }
      busy.value = true;
      error.value = '';
      try {
        const data = await api.create({
          ...draft.value,
          empNumber: Number(draft.value.empNumber),
          reviewerEmpNumber: Number(draft.value.reviewerEmpNumber),
        });
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        selectedId.value = data.id;
        await load();
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    onMounted(load);
    return {
      items,
      employees,
      templates,
      scope,
      total,
      offset,
      limit,
      selectedId,
      isAdmin,
      canCreate,
      legacyUrl,
      loading,
      busy,
      creating,
      dirty,
      error,
      success,
      draft,
      reviewerOptions,
      supervisorNote,
      displayDate,
      statusLabel,
      localUrl,
      load,
      changeScope,
      page,
      openReview,
      cancelCreate,
      chooseEmployee,
      create,
    };
  },
};
</script>
